import {
  Group,
  Box,
  ScrollArea,
  Table,
  Text,
  useMantineTheme,
  ActionIcon,
  Paper,
  Pagination,
  Button,
  TextInput,
  Dialog,
  Select,
  Badge,
} from "@mantine/core";
import {
  Pencil,
  Trash,
  Plus,
  Search,
  Link as IconLink,
} from "tabler-icons-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import {
  useChartsQuery,
  useDeleteChartMutation,
  useCategoryEntityQuery,
  useChartViewsCountEntityQuery,
} from "services/api";
import { INITIAL_PAGINATION_RESPONSE, INITIAL_ENTITY } from "services/api";

import Th from "features/backstage/_shared/Th";
import useTotalPages from "features/backstage/_shared/useTotalPages";

const List = () => {
  const theme = useMantineTheme();

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);

  const [sort, setSort] = useState<string>();
  const [isDescending, setIsDescending] = useState(false);

  const [search, setSearch] = useState<string>();
  const [searchText, setSearchText] = useState<string>("");

  const [categoryId, setCategoryId] = useState<string>();
  const [isHot, setIsHot] = useState<string>();
  const [display, setDisplay] = useState<string>();

  const { data: chartListData = INITIAL_PAGINATION_RESPONSE } = useChartsQuery({
    page,
    perPage,
    search,
    categoryId,
    isHot,
    display,
    sort: isDescending ? `-${sort}` : sort,
  });

  const totalPages = useTotalPages(chartListData.totalCount, perPage);

  const [deleteChart] = useDeleteChartMutation();
  const [deleteChartId, setDeleteChartId] = useState<number>();

  const { data: categoryEntity = INITIAL_ENTITY } = useCategoryEntityQuery();

  const { data: chartViewsCountEntity = INITIAL_ENTITY } =
    useChartViewsCountEntityQuery();

  const categoryOptions = useMemo(
    () => categoryEntity.ids.map((id) => categoryEntity.entities[id]),
    [categoryEntity]
  );

  const handleSort = (sortBy: string) => () => {
    setPage(1);

    if (sortBy === sort) {
      setIsDescending((prev) => !prev);
      return;
    }

    setSort(sortBy);
    setIsDescending(false);
  };

  const rows = chartListData.items.map((x) => (
    <tr key={x.id}>
      <td>
        <Group spacing="sm">
          <Text size="sm" weight={500}>
            {categoryEntity.entities[x.mhid]?.label}
          </Text>
        </Group>
      </td>
      <td>
        <Group spacing="sm">
          <Text size="sm" weight={500}>
            {x.boardName}
          </Text>
        </Group>
      </td>
      <td>
        {x.display ? (
          <Badge
            color="cyan"
            variant={theme.colorScheme === "dark" ? "light" : "outline"}
          >
            {x.display === 2 ? "公開" : "限市府人員"}
          </Badge>
        ) : (
          <Badge
            color="gray"
            variant={theme.colorScheme === "dark" ? "light" : "outline"}
          >
            隱藏下架
          </Badge>
        )}
      </td>
      <td>{chartViewsCountEntity.entities[x.id]?.value}</td>
      <td>{x.isSysHot ? "自動" : "無"}</td>
      <td>{x.isHot ? "手動" : "無"}</td>
      <td>{x.sort}</td>
      <td>{x.publicDate ? new Date(x.publicDate).toLocaleString() : "-"}</td>
      <td>{x.updateDate ? new Date(x.updateDate).toLocaleString() : "-"}</td>
      <td>
        <Group spacing={0} position="right">
          <ActionIcon
            color="gray"
            component={Link}
            to={`/charts/${x.url || x.id}`}
            target="_blank"
          >
            <IconLink size={20} />
          </ActionIcon>

          <ActionIcon color="gray" component={Link} to={`${x.id}/edit`}>
            <Pencil size={20} />
          </ActionIcon>
          <ActionIcon color="red" onClick={() => setDeleteChartId(x.id)}>
            <Trash size={20} />
          </ActionIcon>
        </Group>
      </td>
    </tr>
  ));

  return (
    <Box py={8} px={36}>
      <Group position="apart">
        <h2>儀表板管理</h2>
        <Button
          component={Link}
          to="new"
          leftIcon={<Plus />}
          variant="outline"
          color="cyan"
        >
          新增儀表板
        </Button>
      </Group>
      <Paper
        px={16}
        pt={20}
        pb={8}
        mt={16}
        radius={"lg"}
        shadow={theme.shadows.lg}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setPage(1);
            setSearch(searchText);
          }}
        >
          <Group mb={16}>
            <TextInput
              placeholder="輸入名稱關鍵字進行搜尋"
              icon={<Search size={14} />}
              rightSectionWidth={132}
              style={{
                flex: "50% 0 0",
              }}
              value={searchText}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setSearchText(event.currentTarget.value)
              }
            />
            <Button color="cyan" type="submit">
              搜尋
            </Button>
          </Group>

          <Group mb={16}>
            <Select
              placeholder="儀表板分類"
              clearable
              data={categoryOptions}
              value={categoryId}
              onChange={setCategoryId}
            />

            <Select
              placeholder="顯示設定"
              clearable
              data={[
                {
                  label: "公開",
                  value: "2",
                },
                {
                  label: "限市府人員",
                  value: "1",
                },
                {
                  label: "隱藏下架",
                  value: "0",
                },
              ]}
              value={display}
              onChange={setDisplay}
            />

            <Select
              placeholder="是否熱門"
              clearable
              data={[
                {
                  label: "熱門",
                  value: "true",
                },
                {
                  label: "非熱門",
                  value: "false",
                },
              ]}
              value={isHot}
              onChange={setIsHot}
            />
          </Group>
        </form>
        <ScrollArea>
          <Table sx={{ minWidth: 800 }} verticalSpacing="sm">
            <thead>
              <tr>
                <Th
                  sorted={sort === "mhid"}
                  reversed={isDescending}
                  onSort={handleSort("mhid")}
                >
                  分類
                </Th>
                <Th
                  sorted={sort === "boardName"}
                  reversed={isDescending}
                  onSort={handleSort("boardName")}
                >
                  儀表板名稱
                </Th>
                <Th
                  sorted={sort === "display"}
                  reversed={isDescending}
                  onSort={handleSort("display")}
                >
                  顯示設定
                </Th>
                <th>總瀏覽數</th>
                <Th
                  sorted={sort === "isSysHot"}
                  reversed={isDescending}
                  onSort={handleSort("isSysHot")}
                >
                  熱門(S)
                </Th>
                <Th
                  sorted={sort === "isHot"}
                  reversed={isDescending}
                  onSort={handleSort("isHot")}
                >
                  熱門(M)
                </Th>
                <Th
                  sorted={sort === "sort"}
                  reversed={isDescending}
                  onSort={handleSort("sort")}
                >
                  順序
                </Th>
                <Th
                  sorted={sort === "publicDate"}
                  reversed={isDescending}
                  onSort={handleSort("publicDate")}
                >
                  上架時間
                </Th>
                <Th
                  sorted={sort === "updateDate"}
                  reversed={isDescending}
                  onSort={handleSort("updateDate")}
                >
                  更新時間
                </Th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.length > 0 ? (
                rows
              ) : (
                <tr>
                  <td colSpan={10}>
                    <Text weight={500} align="center">
                      沒有找到相關資料
                    </Text>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </ScrollArea>

        <Group mb={16} position="apart">
          <Pagination
            pt={24}
            pb={16}
            total={totalPages}
            page={page}
            color="cyan"
            onChange={setPage}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Text mr={16}>共 {chartListData.totalCount} 筆資料</Text>

            <Select
              data={[
                { label: "每頁 50 筆", value: "50" },
                { label: "每頁 100 筆", value: "100" },
              ]}
              value={`${perPage}`}
              onChange={(value) => setPerPage(+value)}
            />
          </Box>
        </Group>
      </Paper>

      <Dialog
        opened={deleteChartId !== undefined}
        withCloseButton
        onClose={() => setDeleteChartId(undefined)}
        size="lg"
        radius="md"
      >
        <Group align="center">
          <Text size="sm" style={{ marginBottom: 10 }} weight={500}>
            確定刪除儀表板資料？
          </Text>
          <Button
            color="red"
            onClick={() => {
              deleteChart(deleteChartId);
              setDeleteChartId(undefined);
            }}
          >
            確認刪除
          </Button>
        </Group>
      </Dialog>
    </Box>
  );
};

export default List;
